import { Box } from '@mui/material';
import { useTheme } from '@mui/material/styles';
import Highcharts from 'highcharts';
import HighchartsReact from 'highcharts-react-official';
import { useEffect, useMemo, useRef } from 'react';

const SEVERITY_COLORS = {
  Critical: '#c72929',
  Major: '#c72929',
  Medium: '#f47f1f',
  Minor: '#ffb300',
  Trivial: '#4cae4f',
  Information: '#1975d2',
  Unknown: 'transparent',
};

const roundToDay = (isoString) => {
  const date = new Date(isoString);
  date.setUTCHours(0, 0, 0, 0);
  return date.toISOString();
};

const AlarmSeverityAreaChart = ({
  alarms = [],
  //visibleSeverities = {}
}) => {
  const theme = useTheme();
  const chartRef = useRef(null);
  // const [visibleSeries, setVisibleSeries] = useState({});

  useEffect(() => {
    const severities = [...new Set(alarms.map((a) => a.Severity || 'Unknown'))];
    const initialVisibility = {};
    severities.forEach((s) => {
      initialVisibility[s] = true;
    });
    // setVisibleSeries(initialVisibility);
  }, [alarms]);

  const chartOptions = useMemo(() => {
    if (!alarms.length) return {};

    const dayMs = 24 * 3600 * 1000;
    const allSeverities = [
      ...new Set(alarms.map((a) => a.Severity || 'Unknown')),
    ];
    const timestamps = alarms.map((a) =>
      new Date(roundToDay(a.Created)).getTime(),
    );
    const minTime = Math.min(...timestamps);
    const maxTime = Math.max(...timestamps);

    const timeBuckets = {};
    for (let t = minTime; t <= maxTime; t += dayMs) {
      timeBuckets[t] = {};
      allSeverities.forEach((sev) => {
        timeBuckets[t][sev] = 0;
      });
    }

    alarms.forEach((alarm) => {
      const ts = new Date(roundToDay(alarm.Created)).getTime();
      const sev = alarm.Severity || 'Unknown';
      if (!timeBuckets[ts]) {
        timeBuckets[ts] = {};
        allSeverities.forEach((s) => {
          timeBuckets[ts][s] = 0;
        });
      }
      timeBuckets[ts][sev] += 1;
    });

    const sortedTimestamps = Object.keys(timeBuckets).map(Number).sort();

    const series = allSeverities.map((severity) => ({
      name: severity,
      data: sortedTimestamps.map((ts) => [ts, timeBuckets[ts][severity] || 0]),
      stack: 'severity',
      color: SEVERITY_COLORS[severity] || SEVERITY_COLORS.Unknown,
      // visible: visibleSeverities[severity] !== false,
    }));

    const isDark = theme.palette.mode === 'dark';

    return {
      chart: {
        type: 'area',
        height: 150,
        backgroundColor: isDark ? '#121212' : '#ffffff',
      },
      title: {
        text: '',
        style: {
          fontSize: '14px',
          color: isDark ? '#ffffff' : '#000000',
        },
      },
      xAxis: {
        type: 'datetime',
        title: { text: '' },
        labels: {
          format: '{value:%b %Y}',
          style: { color: isDark ? '#eeeeee' : '#333333' },
        },
        tickInterval: dayMs * 30,
        lineColor: isDark ? '#888' : '#ccc',
        gridLineColor: isDark ? '#333' : '#e6e6e6',
      },
      yAxis: {
        title: { text: '' },
        allowDecimals: false,
        gridLineWidth: 0,
        labels: { style: { color: isDark ? '#eeeeee' : '#333333' } },
      },
      tooltip: {
        shared: true,
        backgroundColor: isDark ? '#333' : '#fff',
        style: { color: isDark ? '#fff' : '#000' },
      },
      plotOptions: {
        area: {
          marker: { enabled: false },
        },
        series: {
          animation: true,
          connectNulls: false,
        },
      },
      legend: {
        enabled: false,
      },
      series,
    };
  }, [alarms, theme.palette.mode]);

  return (
    <HighchartsReact
      highcharts={Highcharts}
      options={chartOptions}
      ref={chartRef}
    />
  );
};

export default AlarmSeverityAreaChart;
